<template>
  <section class="tw:flex tw:max-w-[520px] tw:flex-col tw:gap-7" data-testid="reg-step-account">
    <RegistrationStepHeading :title="$t('registration.account.title')" :lede="$t('registration.account.lede')" />

    <SSOButtons
      v-if="!isSSO"
      :showSSO="showSSO"
      :providers="providers"
      flow="registration"
      :divider-label="$t('sso.divider.registration')"
      :initial-error-code="initialSSOError" />

    <!-- a finished account can be recovered with a password reset, an abandoned one only needs signing into -->
    <LeadAlert v-if="conflict" variant="warning" :title="conflict.message" data-testid="reg-account-conflict">
      <RouterLink :to="{ name: 'login' }" class="tw:font-medium tw:text-primary tw:hover:underline">
        {{ $t('registration.account.sign_in') }}
      </RouterLink>
      <template v-if="conflict.offerReset">
        {{ ' ' }}{{ $t('registration.account.or') }}{{ ' ' }}
        <RouterLink :to="{ name: 'forgotPassword' }" class="tw:font-medium tw:text-primary tw:hover:underline">
          {{ $t('registration.account.reset_password') }}
        </RouterLink>
      </template>
    </LeadAlert>

    <div class="tw:flex tw:flex-col tw:gap-[18px]">
      <LeadInput
        v-model="email"
        type="email"
        autocomplete="email"
        :label="$t('registration.account.email')"
        :errors="fieldErrors.email"
        :readonly="isSSO"
        required
        data-testid="reg-email">
        <template #iconStart><IconMail :size="16" /></template>
        <template #hint>
          <template v-if="isSSO">
            <span data-testid="reg-email-verified-by">
              {{ $t('registration.account.email_verified_by', { provider: ssoProviderName }) }}
            </span>
            {{ ' ' }}
            <button
              type="button"
              class="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:font-medium tw:text-primary tw:hover:underline"
              data-testid="reg-sso-leave"
              @click="leaveSSO">
              {{ $t('registration.account.use_different_email') }}
            </button>
          </template>
          <template v-else>{{ $t('registration.account.email_hint') }}</template>
        </template>
      </LeadInput>

      <!-- the provider's name, for the registrant to confirm or correct; it seeds the profile step -->
      <div v-if="isSSO" class="tw:grid tw:grid-cols-2 tw:gap-4">
        <LeadInput
          v-model="firstName"
          type="text"
          autocomplete="given-name"
          :label="$t('registration.profile.first_name')"
          :errors="fieldErrors.firstName"
          required
          data-testid="reg-sso-first-name" />

        <LeadInput
          v-model="lastName"
          type="text"
          autocomplete="family-name"
          :label="$t('registration.profile.last_name')"
          :errors="fieldErrors.lastName"
          required
          data-testid="reg-sso-last-name" />
      </div>

      <LeadInput
        v-model="mobile"
        type="tel"
        autocomplete="tel"
        :label="$t('registration.account.mobile')"
        :placeholder="$t('registration.account.mobile_placeholder')"
        :errors="fieldErrors.mobile"
        required
        data-testid="reg-mobile">
        <template #iconStart><IconPhone :size="16" /></template>
        <template #hint>{{ $t('registration.account.mobile_hint') }}</template>
      </LeadInput>

      <div v-if="!isSSO" class="tw:flex tw:flex-col tw:gap-2">
        <LeadInput
          v-model="password"
          type="password"
          autocomplete="new-password"
          :label="$t('registration.account.password')"
          :errors="fieldErrors.password"
          required
          data-testid="reg-password">
          <template #iconStart><IconLock :size="16" /></template>
        </LeadInput>

        <PasswordStrengthMeter :password="password" />
      </div>

      <!-- split around the links so each fragment stays translatable -->
      <LeadCheckbox v-model="acceptedTerms" data-testid="reg-terms">
        {{ $t('registration.account.terms_before') }}
        <a :href="TERMS_URL" target="_blank" rel="noopener" class="tw:text-primary tw:hover:underline">{{
          $t('registration.account.terms')
        }}</a>
        {{ $t('registration.account.terms_between') }}
        <a :href="PRIVACY_URL" target="_blank" rel="noopener" class="tw:text-primary tw:hover:underline">{{
          $t('registration.account.privacy')
        }}</a
        >{{ $t('registration.account.terms_after') }}
      </LeadCheckbox>

      <!-- honeypot: off-screen rather than display:none because some bots skip hidden inputs -->
      <label v-if="!isSSO" class="tw:sr-only" aria-hidden="true">
        <span>Company website</span>
        <input v-model="honeypot" type="text" tabindex="-1" autocomplete="off" data-testid="reg-honeypot" />
      </label>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * Step 1 - submitting persists nothing; the server holds the answers and emails a code, so the account is only created
 * in step 2. After a Google or Microsoft sign-in (`?sso=<provider>`) the provider already verified the email: it is
 * locked, no password is asked for, and confirming creates the account and continues at the profile step.
 */
import { IconLock, IconMail, IconPhone } from '@tabler/icons-vue';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

import type { ClientError } from '@/api/client';
import { registrationClient } from '@/api/registration';
import SSOButtons from '@/components/auth/SSOButtons.vue';
import { SSO_PROVIDERS, useSSOErrorFromQuery, useSSOProviders } from '@/composables/useSSOProviders';
import { useUserStore } from '@/stores/user';
import type { RegistrationStepJump } from '@/stores/registration';
import LeadAlert from '@/components/lead-design/LeadAlert.vue';
import LeadCheckbox from '@/components/lead-design/LeadCheckbox.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';
import PasswordStrengthMeter from '@/components/registration/subcomponents/PasswordStrengthMeter.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import { isPasswordAcceptable } from '@/components/registration/subcomponents/passwordStrength';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import {
  readRegistrationErrorBody,
  readRegistrationErrorCode,
  registrationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();
const userStore = useUserStore();
const toast = useToast();
const route = useRoute();
const router = useRouter();
const { providers, showSSO } = useSSOProviders();

/** Switzerland only, so the country is fixed */
const MOBILE_COUNTRY = 'CH';
const MIN_MOBILE_LENGTH = 6;
const TERMS_URL = 'https://www.prospective.ch/agb';
const PRIVACY_URL = 'https://www.prospective.ch/datenschutz';

/** never stored (not in the store or session storage); sent once, then the component is destroyed */
const password = ref('');
const acceptedTerms = ref(false);
const honeypot = ref('');
const conflict = ref<{ message: string; offerReset: boolean } | null>(null);
/** a failure the provider round trip came back with, shown once under the SSO buttons */
const initialSSOError = ref(useSSOErrorFromQuery());
const fieldErrors = reactive<{ email: string; firstName: string; lastName: string; mobile: string; password: string }>({
  email: '',
  firstName: '',
  lastName: '',
  mobile: '',
  password: '',
});

/** unrecognised keys (e.g. `accepted_terms`) are dropped rather than crashing */
const FIELD_ERROR_KEYS: Partial<Record<string, keyof typeof fieldErrors>> = {
  email: 'email',
  first_name: 'firstName',
  last_name: 'lastName',
  mobile: 'mobile',
  password: 'password',
};

/* --------------------------------- COMPUTED --------------------------------- */
// bound to the draft so a reload recovers them; the password never joins them
const email = computed({
  get: () => store.draft.account.email,
  set: (value: string) => {
    store.draft.account.email = value;
  },
});

const mobile = computed({
  get: () => store.draft.account.mobile,
  set: (value: string) => {
    store.draft.account.mobile = value;
  },
});

// the profile step's fields, so a name confirmed here is already filled in there
const firstName = computed({
  get: () => store.draft.profile.firstName,
  set: (value: string) => {
    store.draft.profile.firstName = value;
  },
});

const lastName = computed({
  get: () => store.draft.profile.lastName,
  set: (value: string) => {
    store.draft.profile.lastName = value;
  },
});

const isSSO = computed(() => store.ssoIdentity !== null);

const ssoProviderName = computed(() => (store.ssoIdentity ? SSO_PROVIDERS[store.ssoIdentity.provider].name : ''));

/** the name is only asked for here after an SSO sign-in; otherwise the profile step collects it */
const isNameComplete = computed(() => !isSSO.value || (firstName.value.trim() !== '' && lastName.value.trim() !== ''));

/** gates the button, so it stays quiet while the registrant is still typing */
const isFormValid = computed(
  () =>
    isEmailPlausible(email.value) &&
    isNameComplete.value &&
    mobile.value.trim().length >= MIN_MOBILE_LENGTH &&
    (isSSO.value || isPasswordAcceptable(password.value)) &&
    acceptedTerms.value
);

/* --------------------------------- METHODS ---------------------------------- */
/** catches typos only; rejecting a deliverable address is worse than accepting an undeliverable one */
function isEmailPlausible(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

function clearErrors() {
  conflict.value = null;
  fieldErrors.email = '';
  fieldErrors.firstName = '';
  fieldErrors.lastName = '';
  fieldErrors.mobile = '';
  fieldErrors.password = '';
}

/** runs on commit; returns whether it is safe to call the server */
function validateForSubmit() {
  clearErrors();

  if (!isEmailPlausible(email.value)) fieldErrors.email = translator.get('registration.account.email_invalid');
  if (isSSO.value && !firstName.value.trim())
    fieldErrors.firstName = translator.get('registration.profile.name_required');
  if (isSSO.value && !lastName.value.trim())
    fieldErrors.lastName = translator.get('registration.profile.name_required');
  if (mobile.value.trim().length < MIN_MOBILE_LENGTH)
    fieldErrors.mobile = translator.get('registration.account.mobile_invalid');
  if (!isSSO.value && !isPasswordAcceptable(password.value))
    fieldErrors.password = translator.get('registration.account.password_invalid');

  return Object.values(fieldErrors).every((message) => !message) && acceptedTerms.value;
}

/** drops the `sso` from the url without touching the step */
function removeSSOQuery() {
  const { sso: _left, ...query } = route.query;
  router.replace({ query });
}

/** back to the email and password form, e.g. to register a different address */
function leaveSSO() {
  store.clearSSOIdentity();
  email.value = '';
  clearErrors();
  removeSSOQuery();
}

/** reads the identity back from the session, which also survives a reload of this step */
async function loadSSOIdentity() {
  try {
    const identity = await registrationClient.getSSOIdentity();
    store.ssoIdentity = identity;
    email.value = identity.email;
    // a name already in the draft was typed by the registrant (e.g. before a reload), so it wins over the provider's
    if (!firstName.value.trim()) firstName.value = identity.firstName ?? '';
    if (!lastName.value.trim()) lastName.value = identity.lastName ?? '';
  } catch (error) {
    store.clearSSOIdentity();
    initialSSOError.value = (error as ClientError)?.responseJson?.code ?? 'generic';
    removeSSOQuery();
  }
}

async function submitWithSSO(): Promise<RegistrationStepJump | false> {
  try {
    const response = await registrationClient.createWithSSO({
      mobile: mobile.value.replace(/\s+/g, ''),
      mobileCountry: MOBILE_COUNTRY,
      acceptedTerms: true,
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
    });

    // the account exists and the session is signed in; the email step is skipped since the provider verified it
    store.emailVerified = true;
    store.clearSSOIdentity();
    store.draft.account.email = response.email;
    await userStore.fetchCurrent();

    return { goTo: 'profile' };
  } catch (error) {
    return handleSubmitError(error);
  }
}

async function submit(): Promise<boolean | RegistrationStepJump> {
  if (!validateForSubmit()) return false;

  if (isSSO.value) return submitWithSSO();

  try {
    const response = await registrationClient.create({
      email: email.value.trim(),
      mobile: mobile.value.replace(/\s+/g, ''),
      mobileCountry: MOBILE_COUNTRY,
      password: password.value,
      acceptedTerms: true,
      honeypot: honeypot.value,
    });

    store.setVerification({
      maskedMobile: response.maskedMobile,
      expiresInSeconds: response.expiresInSeconds,
      resendAfterSeconds: response.resendAfterSeconds,
    });
    // the server echoes the trimmed, lowercased address it sent to
    store.draft.account.email = response.email;

    return true;
  } catch (error) {
    return handleSubmitError(error);
  }
}

/** shows a refusal inline where possible; returns false to keep the registrant on the step */
function handleSubmitError(error: unknown): false {
  const code = readRegistrationErrorCode(error);

  // the sign-in on the session expired or was used elsewhere: start over with the provider or the email form
  if (typeof code === 'string' && code.startsWith('sso_')) {
    store.clearSSOIdentity();
    email.value = '';
    initialSSOError.value = code;
    removeSSOQuery();
    return false;
  }

  if (code === 'email_already_registered' || code === 'registration_in_progress') {
    conflict.value = {
      message: translator.get(`registration.account.conflict.${code}`),
      offerReset: code === 'email_already_registered',
    };
    return false;
  }

  // a plain refusal carries none of the codes above and the shared client's toast is off for this call
  const responseJson = readRegistrationErrorBody(error);
  const backendErrors = responseJson?.errors;
  const fieldEntries = Object.entries(backendErrors ?? {}).filter(([, messages]) => messages?.length);

  if (fieldEntries.length) {
    for (const [field, messages] of fieldEntries) {
      const key = FIELD_ERROR_KEYS[field];
      // the server only says the number could not be read; ours names the expected format
      if (key)
        fieldErrors[key] = key === 'mobile' ? translator.get('registration.account.mobile_unreadable') : messages[0];
    }
    return false;
  }

  // no field to pin it to, so show the generic top-level message
  if (responseJson) {
    toast.error(registrationErrorMessage(error, translator.get('common.network_error')));
    return false;
  }

  // anything else is surfaced by the shared client's handler
  throw error;
}

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.account.onContinue = submit;
  allowNext.value = isFormValid.value;

  if (typeof route.query.sso === 'string') {
    loadSSOIdentity();
  } else if (store.ssoIdentity) {
    // the url no longer asks for it, e.g. after navigating back to a plain /register
    store.clearSSOIdentity();
  }
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(isFormValid, (valid) => {
  allowNext.value = valid;
});

// editing after a refusal clears it: the message described the old address
watch([email, mobile], () => {
  if (conflict.value) conflict.value = null;
});
</script>
